import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import CustomCalendar from './CustomCalendar'
import TimeBlockPage from './TimeBlockPage';

// Set the app element for screen readers
Modal.setAppElement('#root');

function MainPage() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [appointmentsForSelectedDay, setAppointmentsForSelectedDay] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [appointments, setAppointments] = useState({});
  const [dateSelectionModalOpen, setDateSelectionModalOpen] = useState(false);
  const [timeSelectionModalOpen, setTimeSelectionModalOpen] = useState(false);
  const [disabledDates, setDisabledDates] = useState({});
  const ID = localStorage.getItem('userID');

  // Fetch disabled/holiday dates from API for the currently shown month/year
  const fetchDisabledDates = async (month = currentMonth, year = currentYear) => {
    try {
      const currDate = `${String(month + 1).padStart(2, '0')}-${year}`; // MM-YYYY
      const response = await fetch(`https://xianhefairy.com/LotusFairy_API/api/Booking/GetHolidayDate?MasterId=${ID}&CurrDate=${currDate}`);
      if (!response.ok) throw new Error('Failed to fetch holiday dates');
      const apiData = await response.json();
      const mapped = {};
      apiData.forEach(d => {
        if (!d.LeaveDate) return;
        const parts = d.LeaveDate.split('-'); // "DD-MM-YYYY"
        if (parts.length !== 3) {
          console.warn('Invalid date format:', d.LeaveDate);
          return;
        }
        const day = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(day) && !isNaN(m) && !isNaN(y) && y > 0) {
          if (!mapped[y]) mapped[y] = {};
          if (!mapped[y][m]) mapped[y][m] = [];
          if (!mapped[y][m].includes(day)) mapped[y][m].push(day);
        }
      });
      setDisabledDates(mapped);
    } catch (error) {
      console.error('Error fetching disabled dates:', error);
    }
  };

  // Function to fetch appointments data
  const fetchAppointmentsData = async () => {
    try {
      const response = await fetch(`https://xianhefairy.com/LotusFairy_API/api/Booking/getBD?ID=${ID}`);
      if (!response.ok) throw new Error('Failed to fetch appointments data');
      const data = await response.json();
      console.group('Raw Appointment API Data');
      data.forEach((evt, idx) => {
        console.log(`#${idx}`, evt.REC_DATE, evt.EMAIL);
      });
      console.groupEnd();
      setAppointments(mapApiDataToAppointments(data));
    } catch (error) {
      console.error('Error fetching appointments data:', error);
    }
  };

  // Function to get days in a given month and year
  const daysInMonth = (month, year) => {
    const days = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate(); // Last day of the month

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    return days;
  };

  // Convert REC_DATE to day and store appointments
  const mapApiDataToAppointments = (apiData) => {
    const appointments = {};
    apiData.forEach(event => {
      const eventDate = new Date(event.REC_DATE);
      const day = eventDate.getDate();
      const month = eventDate.getMonth();
      const year = eventDate.getFullYear();
      const time = eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (!appointments[year]) {
        appointments[year] = {};
      }

      if (!appointments[year][month]) {
        appointments[year][month] = {};
      }

      if (!appointments[year][month][day]) {
        appointments[year][month][day] = [];
      }

      appointments[year][month][day].push({ time, email: event.EMAIL });
    });
    // After constructing, log a summary grouped by day
    try {
      console.group('Mapped Appointments Summary');
      Object.keys(appointments).forEach(y => {
        Object.keys(appointments[y]).forEach(m => {
          Object.keys(appointments[y][m]).forEach(d => {
            const times = appointments[y][m][d].map(a => a.time);
            console.log(`${y}-${Number(m)+1}-${d}:`, times.join(', '));
          });
        });
      });
      console.groupEnd();
    } catch (e) {
      console.warn('Logging appointments summary failed:', e);
    }
    return appointments;
  };

  useEffect(() => {
    // Fetch appointments data
    fetchAppointmentsData();
    // Fetch disabled/holiday dates for the initial month
    fetchDisabledDates();

    // Handle window resize for mobile layout
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Log which days in current month have appointments vs disabled (no appointments)
  useEffect(() => {
    if (!appointments[currentYear] || !appointments[currentYear][currentMonth]) return;
    const daysWithAppts = Object.keys(appointments[currentYear][currentMonth]).map(Number);
    const totalDays = new Date(currentYear, currentMonth + 1, 0).getDate();
    const disabledDays = [];
    for (let d = 1; d <= totalDays; d++) {
      if (!daysWithAppts.includes(d)) disabledDays.push(d);
    }
    console.group(`Month View ${new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}`);
    console.log('Days with appointments:', daysWithAppts);
    console.log('Days disabled (no appointments):', disabledDays);
    daysWithAppts.forEach(d => {
      const entries = appointments[currentYear][currentMonth][d] || [];
      console.group(`  Day ${d}`);
      entries.forEach((e,i) => console.log(`    [${i}] time=${e.time} email=${e.email}`));
      console.groupEnd();
    });
    console.groupEnd();
  }, [appointments, currentMonth, currentYear]);

  // Refresh disabled dates when the visible month/year or the date-selection modal state changes
  useEffect(() => {
    fetchDisabledDates(currentMonth, currentYear);
  }, [currentMonth, currentYear, dateSelectionModalOpen]);

  const openModal = (day) => {
    setSelectedDate(day);
    setAppointmentsForSelectedDay(appointments[currentYear]?.[currentMonth]?.[day] || []);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Function to go to the previous month
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Function to go to the next month
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const [selectedForDisabling, setSelectedForDisabling] = useState([]);

  const openDateSelectionModal = () => {
    setDateSelectionModalOpen(true);
    setSelectedForDisabling([]); // Reset selection
  };
  
  const openTimeSelectionModal = () => {
    setTimeSelectionModalOpen(true);
  };

  const closeDateSelectionModal = () => {
    setDateSelectionModalOpen(false);
  };

  const closeTimeSelectionModal = () => {
    setTimeSelectionModalOpen(false);
  };

  const toggleDateSelection = (date) => {
    setSelectedForDisabling((prev) => 
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  };

  return (
    <div style={{ textAlign: 'center', padding: '20px', height: '100%' }}>
      <h2 style={{ color: 'white' }}>Appointment Schedule</h2>

      <div style={{ marginBottom: '20px' }}>
        <button onClick={goToPreviousMonth} style={{ marginRight: '20px', border: 'none', borderRadius: '10px', color: 'white', background: 'transparent' }}>
          ◄
        </button>
        <span style={{ color: 'white' }}>{new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
        <button onClick={goToNextMonth} style={{ marginLeft: '20px', border: 'none', borderRadius: '10px', color: 'white', background: 'transparent' }}>
          ►
        </button>
      </div>

      <div className="weekday-header">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((weekday) => (
          <div key={weekday} className="weekday-label">{weekday}</div>
        ))}
      </div>

      <div className="calendar-container">
        {Array.from({ length: new Date(currentYear, currentMonth, 1).getDay() }).map((_, i) => (
          <div key={`blank-${i}`} className="calendar-day" style={{ visibility: 'hidden' }} />
        ))}
        {daysInMonth(currentMonth, currentYear).map((day) => {
          const hasAppts = !!appointments[currentYear]?.[currentMonth]?.[day];
          const isDisabled = !!(disabledDates[currentYear] && disabledDates[currentYear][currentMonth] && disabledDates[currentYear][currentMonth].includes(day));
          const bgColor = isDisabled ? '#6c6c6c' : hasAppts ? '#f0f0f0' : '#e0e0e0';
          const cursorStyle = isDisabled || !hasAppts ? 'not-allowed' : 'pointer';
          const textColor = isDisabled ? '#ffffff' : '#000000';

          return (
            <div
              key={day}
              className="calendar-day"
              onClick={() => hasAppts && !isDisabled && openModal(day)}
              style={{ backgroundColor: bgColor, cursor: cursorStyle, color: textColor }}
            >
              <div className="day-number">{day}</div>
              <div className="appointment-count">
                {hasAppts ? `${appointments[currentYear][currentMonth][day].length} Appointments` : ''}
              </div>
            </div>
          );
        })}
      </div>

      <button 
        onClick={openDateSelectionModal} 
        style={{  marginTop: '5%', marginBottom: '20px', padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px' }}
      >
        Disable Dates
      </button>

      <button 
        onClick={openTimeSelectionModal} 
        style={{  marginTop: '5%', marginLeft: '5%', marginBottom: '20px', padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px' }}
      >
        Disable Time
      </button>

      {/* Modal for appointments details */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Appointments Details"
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '90%' : '30%',
            padding: '20px',
            height: 'auto',
          },
        }}
      >
        <h4>Appointments for {selectedDate} {new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long' })}</h4>
        <p>
          <strong>Total Appointments:</strong> {appointmentsForSelectedDay.length}
        </p>
        <ul>
          {appointmentsForSelectedDay.map((appointment, index) => (
            <li key={index}>
              <strong>Time:</strong> {appointment.time} <br />
              <strong>Email:</strong> {appointment.email}
            </li>
          ))}
        </ul>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <button
            onClick={closeModal}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '5px',
            }}
          >
            Close
          </button>
        </div>
      </Modal>

      <Modal 
  isOpen={dateSelectionModalOpen} 
  onRequestClose={closeDateSelectionModal} 
  contentLabel="Select Dates to Disable"
  style={{
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: isMobile ? '90%' : '40%',
      padding: '20px',
      maxHeight: '80%', // Set maximum height
      overflowY: 'auto', // Enable vertical scrolling
    },
  }}
>
  <button
    onClick={() => setDateSelectionModalOpen(false)}
    style={{
      position: 'absolute',
      top: '10px', // Adjust vertical positioning
      right: '10px', // Adjust horizontal positioning
      background: 'none',
      border: 'none',
      fontSize: '18px',
      cursor: 'pointer',
    }}
  >
    <p>X</p>
  </button>
  <CustomCalendar/>
</Modal>

      
      <Modal 
        isOpen={timeSelectionModalOpen} 
        onRequestClose={closeTimeSelectionModal} 
        contentLabel="Select Time to Disable"
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '90%' : '40%',
            padding: '20px',
            maxHeight: '80%', // Set maximum height
            overflowY: 'auto', // Enable vertical scrolling
          },
        }}
      >
        <button
          onClick={() => setTimeSelectionModalOpen(false)}
          style={{
            position: 'absolute',
            top: '10px', // Adjust vertical positioning
            right: '10px', // Adjust horizontal positioning
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
          }}
        >
          <p>X</p>
        </button>
        <TimeBlockPage/>
      </Modal>

      <style>{`
        .weekday-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 10px;
          margin-top: 20px;
        }

        .weekday-label {
          color: white;
          font-weight: bold;
          text-align: center;
        }

        .calendar-container {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 10px;
          margin-top: 10px;
        }

        .calendar-day {
          width: 100%;
          height: 118px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          border-radius: 10px;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s;
        }

        .calendar-day:hover {
          transform: scale(1.05);
        }

        .day-number {
          font-weight: bold;
          text-align: left;
        }

        .appointment-count {
          margin-top: 5px;
          color: black;
          text-align: center;
        }

        /* Keep a grid layout on small screens but reduce sizes and enable horizontal scrolling */
        @media (max-width: 768px) {
          .weekday-header {
            gap: 4px;
            margin-top: 20px;
          }
          .weekday-label {
            font-size: 0.78em;
          }
          .calendar-container {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 4px;
            margin-top: 10px;
            width: 100%;
            max-width: 100%;
            overflow: hidden;
          }
          .calendar-day {
            height: auto;
            min-height: 68px;
            padding: 4px 2px;
            margin-bottom: 0;
            min-width: 0;
            width: 100%;
            box-sizing: border-box;
          }
          .day-number {
            font-size: 0.78em;
            line-height: 1;
          }
          .appointment-count {
            font-size: 0.66em;
            line-height: 1.1;
            word-break: break-word;
          }
        }
      `}</style>
    </div>
  );
}

export default MainPage;
